import { useEffect, useState } from 'react';
// Every content/<lang>.json written by the TYPO3 import becomes its own lazy chunk, so a new
// language only needs its JSON file. The context starts at this folder rather than at ./content
// so the build doesn't fail as long as content/ doesn't exist yet.
const manualFiles = require.context('.', true, /^\.\/content\/[a-z]{2}\.json$/, 'lazy');
const FALLBACK_LANGUAGE = 'de';
const TABLE_SCROLL_CLASS = 'creator-manual-html__table-scroll';
const getManualFileKey = (language) => {
    const availableKeys = manualFiles.keys();
    const candidates = [language, FALLBACK_LANGUAGE].map((lang) => `./content/${lang}.json`);
    return candidates.find((key) => availableKeys.includes(key)) ?? null;
};
const isRecord = (value) => typeof value === 'object' && value !== null;
const isManualSection = (value) => isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.html === 'string';
const isManualChapter = (value) => isRecord(value) &&
    isManualSection(value) &&
    Array.isArray(value.sections) &&
    value.sections.every(isManualSection);
// Only the fields the page renders are checked, so a broken import shows the error state
// instead of crashing the page.
const isCreatorManual = (value) => isRecord(value) &&
    typeof value.language === 'string' &&
    typeof value.title === 'string' &&
    Array.isArray(value.chapters) &&
    value.chapters.every(isManualChapter);
// Wide tables get their own horizontal scroll container instead of stretching the page.
const wrapTables = (html) => html
    .replace(/<table\b/gi, `<div class="${TABLE_SCROLL_CLASS}"><table`)
    .replace(/<\/table>/gi, '</table></div>');
const prepareManual = (manual) => ({
    ...manual,
    chapters: manual.chapters.map((chapter) => ({
        ...chapter,
        html: wrapTables(chapter.html),
        sections: chapter.sections.map((section) => ({ ...section, html: wrapTables(section.html) })),
    })),
});
// Lazy JSON modules resolve to a namespace object with the data under `default`.
const unwrapJsonModule = (module) => isRecord(module) && 'default' in module ? module.default : module;
/**
 * Loads the creator manual for `language`, falling back to German. Resolves to `unavailable`
 * when no manual has been imported yet. The file is only reloaded when the resolved file
 * changes, e.g. switching from EN to CS while both fall back to DE keeps the loaded manual.
 */
export const useCreatorManual = (language) => {
    const fileKey = getManualFileKey(language);
    const [state, setState] = useState({ status: 'loading' });
    useEffect(() => {
        if (!fileKey) {
            return undefined;
        }
        let cancelled = false;
        setState((current) => (current.status === 'loading' ? current : { status: 'loading' }));
        manualFiles(fileKey)
            .then((module) => {
            if (cancelled) {
                return;
            }
            const data = unwrapJsonModule(module);
            setState(isCreatorManual(data)
                ? { status: 'ready', manual: prepareManual(data) }
                : { status: 'error' });
        })
            .catch((error) => {
            console.error('Failed to load the creator manual:', error);
            if (!cancelled) {
                setState({ status: 'error' });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [fileKey]);
    return fileKey ? state : { status: 'unavailable' };
};
